const clonePlainValue = (value) => {
  if (value === undefined || value === null) {
    return value;
  }

  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    if (Array.isArray(value)) {
      return value.map((item) => clonePlainValue(item));
    }
    if (typeof value === 'object') {
      return { ...value };
    }
    return value;
  }
};

const WHATSAPP_CONVERSATION_CHANNEL_TYPE = 'Channel::Whatsapp';

const cloneConversationCacheList = (conversations = []) =>
  (Array.isArray(conversations) ? conversations : []).map((conversation) =>
    clonePlainValue(conversation)
  );

const encodeConversationCacheKeyPayload = (payload = '') => {
  if (typeof btoa === 'function') {
    return btoa(payload);
  }

  if (typeof Buffer !== 'undefined') {
    return Buffer.from(payload, 'utf8').toString('base64');
  }

  return payload;
};

const normalizeConversationCacheLabels = (labels) => {
  if (!labels) return null;
  if (Array.isArray(labels)) {
    const normalizedLabels = labels.map((label) => String(label)).sort();
    return normalizedLabels.length ? normalizedLabels.join(',') : null;
  }
  return String(labels);
};

const normalizeConversationCacheKeyFilters = (inboxId, filters = {}) => {
  const normalizedFilters = {
    inboxId: inboxId ? String(inboxId) : 'all',
    status: filters.status || 'open',
    assigneeType: filters.assigneeType || 'all',
    sortBy: filters.sortBy || 'last_activity_at_desc',
    page: Number(filters.page || 1),
    per_page: Number(filters.per_page || 30),
    labels: normalizeConversationCacheLabels(filters.labels),
    conversationType: filters.conversationType || null,
    assignee_id: filters.assignee_id ? String(filters.assignee_id) : null,
    contact_id: filters.contact_id ? String(filters.contact_id) : null,
    include_labeled: Boolean(filters.include_labeled),
    has_priority: Boolean(filters.has_priority),
    label_id: filters.label_id ? String(filters.label_id) : null,
    label_source_id: filters.label_source_id
      ? String(filters.label_source_id)
      : null,
  };

  return Object.keys(normalizedFilters)
    .sort()
    .reduce((result, key) => {
      if (
        normalizedFilters[key] !== undefined &&
        normalizedFilters[key] !== null
      ) {
        result[key] = normalizedFilters[key];
      }
      return result;
    }, {});
};

const buildConversationCacheKey = (inboxId, filters = {}) => {
  const sortedFilters = normalizeConversationCacheKeyFilters(inboxId, filters);
  const filterString = JSON.stringify(sortedFilters);
  const criticalParts = [
    sortedFilters.inboxId,
    sortedFilters.labels || 'no-label',
    sortedFilters.label_id || 'no-lid',
    sortedFilters.status,
    sortedFilters.assigneeType,
  ].join('-');
  const hashBase = encodeConversationCacheKeyPayload(filterString).replace(
    /[+/=]/g,
    ''
  );
  const shortHash = hashBase.substring(0, 12);

  return `${criticalParts}-${shortHash}`;
};

const isConversationCacheOptimizationKeyForInbox = ({
  cacheKey,
  inboxId,
  inboxType,
}) => {
  if (cacheKey === null || cacheKey === undefined) return false;
  if (inboxId === null || inboxId === undefined) return false;

  const normalizedCacheKey = String(cacheKey);
  const normalizedInboxId = String(inboxId);

  // Preserve the legacy optimization contract for every channel.
  if (normalizedCacheKey.startsWith(`${normalizedInboxId}:`)) return true;

  // Current generated keys use `${inboxId}-...`. Limit compatibility with
  // that format to WhatsApp so cache policy for every other channel is stable.
  return (
    inboxType === WHATSAPP_CONVERSATION_CHANNEL_TYPE &&
    normalizedCacheKey.startsWith(`${normalizedInboxId}-`)
  );
};

const buildConversationStatsMetaFromCache = (cache = {}) => {
  if (cache.meta && typeof cache.meta === 'object') {
    return clonePlainValue(cache.meta);
  }

  if (cache.totalCount !== undefined && cache.totalCount !== null) {
    return { all_count: cache.totalCount };
  }

  if (Array.isArray(cache.conversations)) {
    return { all_count: cache.conversations.length };
  }

  return null;
};

export {
  WHATSAPP_CONVERSATION_CHANNEL_TYPE,
  buildConversationCacheKey,
  buildConversationStatsMetaFromCache,
  cloneConversationCacheList,
  clonePlainValue,
  isConversationCacheOptimizationKeyForInbox,
  normalizeConversationCacheKeyFilters,
};
